import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import {
  Save, AlertCircle, AlertTriangle, CheckCircle, Loader2, Truck, Clock, RefreshCw, Link2, Unlink,
  Percent, TrendingUp, Euro, Calculator, Users, Crown, Plus, Trash2, Info
} from 'lucide-react'
import { useToast } from '@/components/ui/Toast'

interface FactusolSupplier {
  code: string
  name: string
}

// Delivery time config per language: {lang_id: text}
interface DeliveryTimeConfig {
  in_stock: Record<string, string>
  out_of_stock: Record<string, string>
}

// Pricing types
interface PricingTier {
  min: number
  max: number
  margin_percent: number
}

interface SupplierPricingConfig {
  vat_rate: number
  tiers_pvp: PricingTier[]
  tiers_pvd: PricingTier[]
  tiers_vip: PricingTier[]
}

type TariffType = 'pvp' | 'pvd' | 'vip'

// Default pricing config for new suppliers (empty - family margins apply until configured)
const DEFAULT_PRICING_CONFIG: SupplierPricingConfig = {
  vat_rate: 21,
  tiers_pvp: [],
  tiers_pvd: [],
  tiers_vip: [],
}

// Known catalog suppliers
const CATALOG_SUPPLIERS = [
  { code: 'ITSYS', name: 'ITSys' },
  // Add more catalog suppliers here as needed
]

type SupplierTabType = 'delivery' | 'mapping' | 'margins'

export default function SuppliersConfigPage() {
  const toast = useToast()
  const queryClient = useQueryClient()

  // Selected supplier
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null)

  // Active tab within supplier (internal tabs)
  const [activeSupplierTab, setActiveSupplierTab] = useState<SupplierTabType>('delivery')

  // Supplier delivery time state (now with per-language support)
  const [supplierDeliveryTimes, setSupplierDeliveryTimes] = useState<Record<string, DeliveryTimeConfig>>({})
  const [supplierDeliverySaveStatus, setSupplierDeliverySaveStatus] = useState<Record<string, 'saving' | 'saved' | 'error'>>({})
  const [isUpdatingSupplierProducts, setIsUpdatingSupplierProducts] = useState(false)

  // Catalog to Factusol mapping state
  const [catalogMappings, setCatalogMappings] = useState<Record<string, string>>({})
  const [mappingSaveStatus, setMappingSaveStatus] = useState<Record<string, 'saving' | 'saved' | 'error'>>({})

  // Pricing margins state
  const [supplierPricingConfigs, setSupplierPricingConfigs] = useState<Record<string, SupplierPricingConfig>>({})
  const [activeTariffTab, setActiveTariffTab] = useState<TariffType>('pvp')
  const [isPricingDirty, setIsPricingDirty] = useState(false)
  const [pricingSaveStatus, setPricingSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')

  // Get available languages
  const languagesQuery = useQuery({
    queryKey: ['prestashop-languages'],
    queryFn: async () => {
      const res = await axios.get('/api/family-config/supplier-delivery-time/languages')
      return res.data.languages as Record<string, string>
    }
  })

  // Get suppliers from Factusol
  const suppliersQuery = useQuery({
    queryKey: ['factusol-suppliers'],
    queryFn: async () => {
      const res = await axios.get('/api/product-ai/factusol/suppliers')
      return (res.data.suppliers || []).map((s: any) => ({
        code: String(s.CODPRO),
        name: s.NOFPRO || s.NOCPRO || `Proveedor ${s.CODPRO}`
      })) as FactusolSupplier[]
    }
  })

  // Get supplier delivery times
  const supplierDeliveryTimesQuery = useQuery({
    queryKey: ['supplier-delivery-times'],
    queryFn: async () => {
      const res = await axios.get('/api/family-config/supplier-delivery-time')
      return res.data as Record<string, DeliveryTimeConfig>
    }
  })

  // Get catalog to Factusol supplier mappings
  const catalogMappingsQuery = useQuery({
    queryKey: ['catalog-supplier-mappings'],
    queryFn: async () => {
      const res = await axios.get('/api/family-config/supplier-mapping')
      return (res.data.mappings || {}) as Record<string, string>
    }
  })

  // Get all supplier pricing configurations
  const supplierPricingConfigsQuery = useQuery({
    queryKey: ['supplier-pricing-configs'],
    queryFn: async () => {
      const res = await axios.get('/api/pricing/supplier-configs')
      return res.data as Record<string, SupplierPricingConfig>
    }
  })

  // Initialize supplier delivery times when data loads
  useEffect(() => {
    if (supplierDeliveryTimesQuery.data) {
      setSupplierDeliveryTimes(supplierDeliveryTimesQuery.data)
    }
  }, [supplierDeliveryTimesQuery.data])

  // Initialize catalog mappings when data loads
  useEffect(() => {
    if (catalogMappingsQuery.data) {
      setCatalogMappings(catalogMappingsQuery.data)
    }
  }, [catalogMappingsQuery.data])

  // Initialize supplier pricing configs when data loads
  useEffect(() => {
    if (supplierPricingConfigsQuery.data) {
      setSupplierPricingConfigs(supplierPricingConfigsQuery.data)
    }
  }, [supplierPricingConfigsQuery.data])

  // Save supplier delivery time
  const saveSupplierDeliveryTimeMutation = useMutation({
    mutationFn: async ({ supplierCode, config }: { supplierCode: string, config: DeliveryTimeConfig }) => {
      await axios.post(`/api/family-config/supplier-delivery-time/${supplierCode}`, config)
    },
    onMutate: ({ supplierCode }) => {
      setSupplierDeliverySaveStatus(prev => ({ ...prev, [supplierCode]: 'saving' }))
    },
    onSuccess: (_, { supplierCode }) => {
      setSupplierDeliverySaveStatus(prev => ({ ...prev, [supplierCode]: 'saved' }))
      queryClient.invalidateQueries({ queryKey: ['supplier-delivery-times'] })
      setTimeout(() => {
        setSupplierDeliverySaveStatus(prev => {
          const newStatus = { ...prev }
          delete newStatus[supplierCode]
          return newStatus
        })
      }, 2000)
    },
    onError: (_, { supplierCode }) => {
      setSupplierDeliverySaveStatus(prev => ({ ...prev, [supplierCode]: 'error' }))
    },
  })

  // Update all products delivery time for a supplier
  const updateSupplierProductsDeliveryTimeMutation = useMutation({
    mutationFn: async (supplierCode: string) => {
      const res = await axios.post(`/api/family-config/supplier-delivery-time/${supplierCode}/update-products`)
      return res.data
    },
    onMutate: () => {
      setIsUpdatingSupplierProducts(true)
    },
    onSuccess: (data) => {
      setIsUpdatingSupplierProducts(false)
      toast.success(
        'Productos actualizados',
        `Se ha actualizado el tiempo de entrega en ${data.updated_count} productos`
      )
    },
    onError: (error: any) => {
      setIsUpdatingSupplierProducts(false)
      toast.error(
        'Error',
        error.response?.data?.detail || 'No se pudieron actualizar los productos'
      )
    },
  })

  // Save catalog to Factusol mapping
  const saveCatalogMappingMutation = useMutation({
    mutationFn: async ({ catalogCode, factusolCode }: { catalogCode: string, factusolCode: string }) => {
      await axios.post(`/api/family-config/supplier-mapping/${catalogCode}`, { factusol_code: factusolCode })
    },
    onMutate: ({ catalogCode }) => {
      setMappingSaveStatus(prev => ({ ...prev, [catalogCode]: 'saving' }))
    },
    onSuccess: (_, { catalogCode }) => {
      setMappingSaveStatus(prev => ({ ...prev, [catalogCode]: 'saved' }))
      queryClient.invalidateQueries({ queryKey: ['catalog-supplier-mappings'] })
      toast.success('Vinculacion guardada', `El proveedor de catalogo se ha vinculado correctamente`)
      setTimeout(() => {
        setMappingSaveStatus(prev => {
          const newStatus = { ...prev }
          delete newStatus[catalogCode]
          return newStatus
        })
      }, 2000)
    },
    onError: (_, { catalogCode }) => {
      setMappingSaveStatus(prev => ({ ...prev, [catalogCode]: 'error' }))
      toast.error('Error', 'No se pudo guardar la vinculacion')
    },
  })

  // Delete catalog to Factusol mapping
  const deleteCatalogMappingMutation = useMutation({
    mutationFn: async (catalogCode: string) => {
      await axios.delete(`/api/family-config/supplier-mapping/${catalogCode}`)
    },
    onSuccess: (_, catalogCode) => {
      setCatalogMappings(prev => {
        const newMappings = { ...prev }
        delete newMappings[catalogCode]
        return newMappings
      })
      queryClient.invalidateQueries({ queryKey: ['catalog-supplier-mappings'] })
      toast.success('Vinculacion eliminada', 'La vinculacion se ha eliminado correctamente')
    },
    onError: () => {
      toast.error('Error', 'No se pudo eliminar la vinculacion')
    },
  })

  // Save supplier pricing configuration
  const saveSupplierPricingMutation = useMutation({
    mutationFn: async ({ supplierCode, config }: { supplierCode: string, config: SupplierPricingConfig }) => {
      await axios.post(`/api/pricing/supplier-configs/${supplierCode}`, config)
    },
    onMutate: () => {
      setPricingSaveStatus('saving')
    },
    onSuccess: () => {
      setPricingSaveStatus('saved')
      setIsPricingDirty(false)
      queryClient.invalidateQueries({ queryKey: ['supplier-pricing-configs'] })
      toast.success('Configuracion guardada', 'Los margenes del proveedor se han guardado correctamente')
      setTimeout(() => setPricingSaveStatus('idle'), 2000)
    },
    onError: () => {
      setPricingSaveStatus('error')
      toast.error('Error', 'No se pudieron guardar los margenes')
    },
  })

  // Delete supplier pricing configuration
  const deleteSupplierPricingMutation = useMutation({
    mutationFn: async (supplierCode: string) => {
      await axios.delete(`/api/pricing/supplier-configs/${supplierCode}`)
    },
    onSuccess: (_, supplierCode) => {
      setSupplierPricingConfigs(prev => {
        const newConfigs = { ...prev }
        delete newConfigs[supplierCode]
        return newConfigs
      })
      setIsPricingDirty(false)
      queryClient.invalidateQueries({ queryKey: ['supplier-pricing-configs'] })
      toast.success('Configuracion eliminada', 'El proveedor ahora usa los margenes de familia o globales')
    },
    onError: () => {
      toast.error('Error', 'No se pudo eliminar la configuracion')
    },
  })

  const handleSaveSupplierDeliveryTime = () => {
    if (!selectedSupplier) return
    const config = supplierDeliveryTimes[selectedSupplier] ?? { in_stock: {}, out_of_stock: {} }
    saveSupplierDeliveryTimeMutation.mutate({ supplierCode: selectedSupplier, config })
  }

  const handleUpdateSupplierProductsDeliveryTime = () => {
    if (!selectedSupplier) return
    updateSupplierProductsDeliveryTimeMutation.mutate(selectedSupplier)
  }

  const handleSaveCatalogMapping = (catalogCode: string) => {
    const factusolCode = catalogMappings[catalogCode]
    if (!factusolCode) {
      toast.error('Error', 'Selecciona un proveedor Factusol')
      return
    }
    saveCatalogMappingMutation.mutate({ catalogCode, factusolCode })
  }

  const handleDeleteCatalogMapping = (catalogCode: string) => {
    deleteCatalogMappingMutation.mutate(catalogCode)
  }

  // Pricing handlers
  const getCurrentPricingConfig = (): SupplierPricingConfig => {
    if (!selectedSupplier) return DEFAULT_PRICING_CONFIG
    return supplierPricingConfigs[selectedSupplier] ?? DEFAULT_PRICING_CONFIG
  }

  const handlePricingTierChange = (index: number, field: keyof PricingTier, value: string) => {
    if (!selectedSupplier) return
    const tierKey = `tiers_${activeTariffTab}` as keyof SupplierPricingConfig
    const currentConfig = getCurrentPricingConfig()
    const currentTiers = [...(currentConfig[tierKey] as PricingTier[])]
    currentTiers[index] = { ...currentTiers[index], [field]: parseFloat(value) || 0 }
    setSupplierPricingConfigs(prev => ({
      ...prev,
      [selectedSupplier]: { ...currentConfig, [tierKey]: currentTiers }
    }))
    setIsPricingDirty(true)
  }

  const handleAddPricingTier = () => {
    if (!selectedSupplier) return
    const tierKey = `tiers_${activeTariffTab}` as keyof SupplierPricingConfig
    const currentConfig = getCurrentPricingConfig()
    const currentTiers = currentConfig[tierKey] as PricingTier[]
    const lastMax = currentTiers.length > 0 ? currentTiers[currentTiers.length - 1].max : 0
    const newTier: PricingTier = { min: lastMax, max: lastMax + 100, margin_percent: 20 }
    setSupplierPricingConfigs(prev => ({
      ...prev,
      [selectedSupplier]: { ...currentConfig, [tierKey]: [...currentTiers, newTier] }
    }))
    setIsPricingDirty(true)
  }

  const handleRemovePricingTier = (index: number) => {
    if (!selectedSupplier) return
    const tierKey = `tiers_${activeTariffTab}` as keyof SupplierPricingConfig
    const currentConfig = getCurrentPricingConfig()
    const currentTiers = (currentConfig[tierKey] as PricingTier[]).filter((_, i) => i !== index)
    setSupplierPricingConfigs(prev => ({
      ...prev,
      [selectedSupplier]: { ...currentConfig, [tierKey]: currentTiers }
    }))
    setIsPricingDirty(true)
  }

  const handleSaveSupplierPricing = () => {
    if (!selectedSupplier) return
    const config = getCurrentPricingConfig()
    saveSupplierPricingMutation.mutate({ supplierCode: selectedSupplier, config })
  }

  const handleDeleteSupplierPricing = () => {
    if (!selectedSupplier) return
    if (window.confirm('¿Eliminar la configuracion de margenes de este proveedor? Se usaran los margenes de familia o globales.')) {
      deleteSupplierPricingMutation.mutate(selectedSupplier)
    }
  }

  const hasSupplierPricingConfig = (code: string) => {
    const config = supplierPricingConfigs[code]
    if (!config) return false
    // Check if any tariff has tiers defined
    return config.tiers_pvp.length > 0 || config.tiers_pvd.length > 0 || config.tiers_vip.length > 0
  }

  const suppliers = Array.isArray(suppliersQuery.data) ? suppliersQuery.data : []
  const selectedSupplierData = suppliers.find(s => s.code === selectedSupplier)

  if (suppliersQuery.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-primary-600 animate-spin" />
          <p className="text-slate-500 font-medium">Cargando proveedores...</p>
        </div>
      </div>
    )
  }

  if (suppliersQuery.isError) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="p-3 bg-danger-100 dark:bg-danger-900/30 rounded-full">
            <AlertTriangle className="w-10 h-10 text-danger-600" />
          </div>
          <div>
            <p className="text-lg font-medium text-slate-900 dark:text-white">Error al cargar proveedores</p>
            <p className="text-slate-500 mt-1">No se pudo conectar con el servidor. Verifica que el backend esté corriendo.</p>
          </div>
          <button
            onClick={() => suppliersQuery.refetch()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  // Check which catalog suppliers are mapped to the selected Factusol supplier
  const getMappedCatalogs = (supplierCode: string) => {
    return CATALOG_SUPPLIERS.filter(cat => catalogMappings[cat.code] === supplierCode)
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <span className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
          <Truck className="w-8 h-8 text-purple-600 dark:text-purple-400" />
        </span>
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Proveedores</h1>
          <p className="text-slate-600 dark:text-slate-400">Selecciona un proveedor para configurar tiempos de entrega, vinculaciones y margenes</p>
        </div>
      </div>

      {/* Main Layout: Supplier List + Detail Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Supplier List (Left Column - Always visible) */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-4 sticky top-4">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <Truck className="w-5 h-5 text-purple-500" />
              Proveedores
            </h2>
            <div className="space-y-1 max-h-[600px] overflow-y-auto">
              {suppliers.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">
                  No se encontraron proveedores
                </p>
              ) : (
                suppliers.map((supplier) => {
                  const isSelected = selectedSupplier === supplier.code
                  const hasDeliveryConfig = Object.values(supplierDeliveryTimes[supplier.code]?.in_stock ?? {}).some(v => v) ||
                                           Object.values(supplierDeliveryTimes[supplier.code]?.out_of_stock ?? {}).some(v => v)
                  const hasPricingConfig = hasSupplierPricingConfig(supplier.code)
                  const hasMappedCatalogs = getMappedCatalogs(supplier.code).length > 0
                  const hasAnyConfig = hasDeliveryConfig || hasPricingConfig || hasMappedCatalogs
                  return (
                    <button
                      key={supplier.code}
                      onClick={() => {
                        setSelectedSupplier(supplier.code)
                        setIsPricingDirty(false)
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                        isSelected
                          ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 border border-purple-300 dark:border-purple-700'
                          : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-mono text-xs text-slate-500 dark:text-slate-400 shrink-0">
                            {supplier.code}
                          </span>
                          <span className="truncate text-sm">{supplier.name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          {hasDeliveryConfig && (
                            <span className="w-2 h-2 bg-green-500 rounded-full shrink-0" title="Tiempo entrega configurado" />
                          )}
                          {hasPricingConfig && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full shrink-0" title="Margenes configurados" />
                          )}
                          {hasMappedCatalogs && (
                            <span className="w-2 h-2 bg-purple-500 rounded-full shrink-0" title="Catalogos vinculados" />
                          )}
                        </div>
                      </div>
                    </button>
                  )
                })
              )}
            </div>
          </div>
        </div>

        {/* Supplier Configuration Panel (Right Column) */}
        <div className="lg:col-span-3">
          {!selectedSupplier ? (
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-12 text-center">
              <Truck className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Selecciona un proveedor
              </h3>
              <p className="text-slate-500 dark:text-slate-400">
                Elige un proveedor del panel izquierdo para configurar sus opciones
              </p>
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
              {/* Supplier Header with internal tabs */}
              <div className="border-b border-slate-200 dark:border-slate-700 p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="text-sm text-slate-500 dark:text-slate-400">Configurando proveedor:</span>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                      <span className="font-mono text-purple-600 dark:text-purple-400 mr-2">{selectedSupplier}</span>
                      {selectedSupplierData?.name}
                    </h2>
                  </div>
                  {activeSupplierTab === 'margins' && hasSupplierPricingConfig(selectedSupplier) && (
                    <button
                      onClick={handleDeleteSupplierPricing}
                      className="flex items-center gap-2 px-4 py-2 bg-danger-100 dark:bg-danger-900/30 text-danger-600 dark:text-danger-400 rounded-lg hover:bg-danger-200 dark:hover:bg-danger-900/50 transition-colors text-sm"
                      title="Eliminar configuracion y usar margenes de familia/global"
                    >
                      <Trash2 className="w-4 h-4" />
                      Eliminar Margenes
                    </button>
                  )}
                </div>

                {/* Internal Tabs */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveSupplierTab('delivery')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                      activeSupplierTab === 'delivery'
                        ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 border border-purple-300 dark:border-purple-700'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    <Clock className="w-4 h-4" />
                    Tiempos de Entrega
                  </button>
                  <button
                    onClick={() => setActiveSupplierTab('mapping')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                      activeSupplierTab === 'mapping'
                        ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 border border-purple-300 dark:border-purple-700'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    <Link2 className="w-4 h-4" />
                    Vinculacion Catalogos
                  </button>
                  <button
                    onClick={() => setActiveSupplierTab('margins')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                      activeSupplierTab === 'margins'
                        ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 border border-purple-300 dark:border-purple-700'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    <Percent className="w-4 h-4" />
                    Margenes de Precios
                  </button>
                </div>
              </div>

              {/* Tab Content: Delivery Time */}
              {activeSupplierTab === 'delivery' && (
                <div className="p-6 space-y-6">
                  <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
                    <p className="text-sm text-purple-700 dark:text-purple-300">
                      Configura los textos de tiempo de entrega que se mostraran en PrestaShop para los productos de este proveedor.
                      Estos valores se aplicaran automaticamente al crear nuevos productos.
                    </p>
                  </div>

                  {/* Fields per language */}
                  {languagesQuery.data && Object.entries(languagesQuery.data).map(([langId, langName]) => (
                    <div key={langId} className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                      <div className="bg-slate-100 dark:bg-slate-700 px-4 py-2">
                        <h4 className="font-medium text-slate-800 dark:text-gray-200">{langName}</h4>
                      </div>
                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* In Stock Field */}
                        <div>
                          <label className="block text-xs font-medium text-green-700 dark:text-green-300 mb-1 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Con stock (delivery_in_stock)
                          </label>
                          <input
                            type="text"
                            value={supplierDeliveryTimes[selectedSupplier]?.in_stock?.[langId] ?? ''}
                            onChange={(e) =>
                              setSupplierDeliveryTimes(prev => ({
                                ...prev,
                                [selectedSupplier]: {
                                  in_stock: {
                                    ...(prev[selectedSupplier]?.in_stock ?? {}),
                                    [langId]: e.target.value
                                  },
                                  out_of_stock: prev[selectedSupplier]?.out_of_stock ?? {}
                                }
                              }))
                            }
                            placeholder={langId === '1' ? 'Ej: Entrega en 24-48h' : langId === '3' ? 'E.g.: Delivery in 24-48h' : ''}
                            className="w-full px-3 py-2 text-sm border border-green-300 dark:border-green-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          />
                        </div>

                        {/* Out of Stock Field */}
                        <div>
                          <label className="block text-xs font-medium text-orange-700 dark:text-orange-300 mb-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Sin stock (delivery_out_stock)
                          </label>
                          <input
                            type="text"
                            value={supplierDeliveryTimes[selectedSupplier]?.out_of_stock?.[langId] ?? ''}
                            onChange={(e) =>
                              setSupplierDeliveryTimes(prev => ({
                                ...prev,
                                [selectedSupplier]: {
                                  in_stock: prev[selectedSupplier]?.in_stock ?? {},
                                  out_of_stock: {
                                    ...(prev[selectedSupplier]?.out_of_stock ?? {}),
                                    [langId]: e.target.value
                                  }
                                }
                              }))
                            }
                            placeholder={langId === '1' ? 'Ej: de 10 a 15 días' : langId === '3' ? 'E.g.: 10 to 15 days' : ''}
                            className="w-full px-3 py-2 text-sm border border-orange-300 dark:border-orange-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Preview - show Spanish values as example */}
                  {(Object.values(supplierDeliveryTimes[selectedSupplier]?.in_stock ?? {}).some(v => v) ||
                    Object.values(supplierDeliveryTimes[selectedSupplier]?.out_of_stock ?? {}).some(v => v)) && (
                    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">Vista previa (Español):</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {supplierDeliveryTimes[selectedSupplier]?.in_stock?.['1'] && (
                          <div className="bg-white dark:bg-slate-800 border border-green-200 dark:border-green-700 rounded-lg p-3">
                            <span className="text-xs text-success-600 dark:text-success-400 font-medium">Con stock:</span>
                            <div className="flex items-center gap-2 mt-1">
                              <CheckCircle className="w-4 h-4 text-success-500" />
                              <span className="text-sm text-slate-700 dark:text-slate-300">
                                {supplierDeliveryTimes[selectedSupplier]?.in_stock?.['1']}
                              </span>
                            </div>
                          </div>
                        )}
                        {supplierDeliveryTimes[selectedSupplier]?.out_of_stock?.['1'] && (
                          <div className="bg-white dark:bg-slate-800 border border-orange-200 dark:border-orange-700 rounded-lg p-3">
                            <span className="text-xs text-orange-600 dark:text-orange-400 font-medium">Sin stock:</span>
                            <div className="flex items-center gap-2 mt-1">
                              <Clock className="w-4 h-4 text-orange-500" />
                              <span className="text-sm text-slate-700 dark:text-slate-300">
                                {supplierDeliveryTimes[selectedSupplier]?.out_of_stock?.['1']}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-2">
                      {supplierDeliverySaveStatus[selectedSupplier] === 'saving' && (
                        <span className="text-primary-500 text-sm flex items-center gap-1">
                          <Loader2 className="w-4 h-4 animate-spin" /> Guardando...
                        </span>
                      )}
                      {supplierDeliverySaveStatus[selectedSupplier] === 'saved' && (
                        <span className="text-success-500 text-sm flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" /> Guardado
                        </span>
                      )}
                      {supplierDeliverySaveStatus[selectedSupplier] === 'error' && (
                        <span className="text-danger-500 text-sm flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" /> Error
                        </span>
                      )}
                    </div>
                    <button
                      onClick={handleSaveSupplierDeliveryTime}
                      disabled={saveSupplierDeliveryTimeMutation.isPending}
                      className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Save className="w-4 h-4" />
                      Guardar Configuracion
                    </button>
                  </div>

                  {/* Update Products Button */}
                  {(Object.values(supplierDeliveryTimes[selectedSupplier]?.in_stock ?? {}).some(v => v) ||
                    Object.values(supplierDeliveryTimes[selectedSupplier]?.out_of_stock ?? {}).some(v => v)) && (
                    <div className="bg-primary-50 dark:bg-primary-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-blue-800 dark:text-blue-200">Actualizar productos existentes</h4>
                          <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                            Aplica estos tiempos de entrega a todos los productos del proveedor {selectedSupplierData?.name} en PrestaShop.
                          </p>
                        </div>
                        <button
                          onClick={handleUpdateSupplierProductsDeliveryTime}
                          disabled={isUpdatingSupplierProducts}
                          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shrink-0"
                        >
                          {isUpdatingSupplierProducts ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                          Actualizar Productos
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tab Content: Catalog Mapping */}
              {activeSupplierTab === 'mapping' && (
                <div className="p-6 space-y-6">
                  <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
                    <p className="text-sm text-purple-700 dark:text-purple-300">
                      Vincula los proveedores de los catalogos externos (como ITSys) con este proveedor de Factusol.
                      Esto permite aplicar automaticamente los tiempos de entrega y margenes cuando se crean productos desde catalogos externos.
                    </p>
                  </div>

                  {/* Show catalogs mapped to this supplier */}
                  {(() => {
                    const mappedCatalogs = getMappedCatalogs(selectedSupplier)
                    return (
                      <div className="space-y-4">
                        {mappedCatalogs.length > 0 ? (
                          <>
                            <h4 className="font-medium text-slate-900 dark:text-white flex items-center gap-2">
                              <Link2 className="w-4 h-4 text-green-500" />
                              Catalogos vinculados a este proveedor:
                            </h4>
                            {mappedCatalogs.map(cat => (
                              <div key={cat.code} className="p-4 rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="p-2 bg-green-100 dark:bg-green-900/50 rounded-lg">
                                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                                    </div>
                                    <div>
                                      <h5 className="font-semibold text-slate-900 dark:text-white">{cat.name}</h5>
                                      <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">Codigo: {cat.code}</p>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleDeleteCatalogMapping(cat.code)}
                                    disabled={deleteCatalogMappingMutation.isPending}
                                    className="flex items-center gap-1 px-3 py-2 bg-danger-100 dark:bg-danger-900/30 text-danger-600 dark:text-danger-400 rounded-lg text-sm font-medium hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Desvincular catalogo"
                                  >
                                    <Unlink className="w-4 h-4" />
                                    Desvincular
                                  </button>
                                </div>
                              </div>
                            ))}
                          </>
                        ) : (
                          <div className="text-center py-8 text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-700">
                            <Unlink className="w-10 h-10 mx-auto mb-3 opacity-50" />
                            <p className="text-sm font-medium">No hay catalogos vinculados a este proveedor</p>
                            <p className="text-xs mt-1">Usa el selector de abajo para vincular un catalogo</p>
                          </div>
                        )}

                        {/* Selector to add new mapping */}
                        <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                          <h4 className="font-medium text-slate-900 dark:text-white mb-3">Vincular nuevo catalogo:</h4>
                          <div className="space-y-3">
                            {CATALOG_SUPPLIERS.filter(cat => catalogMappings[cat.code] !== selectedSupplier).map((catalogSupplier) => {
                              const currentMapping = catalogMappings[catalogSupplier.code]
                              const isAlreadyMapped = !!currentMapping
                              const saveStatus = mappingSaveStatus[catalogSupplier.code]

                              return (
                                <div
                                  key={catalogSupplier.code}
                                  className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50"
                                >
                                  <div className="flex items-center justify-between flex-wrap gap-4">
                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                                        <Truck className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                                      </div>
                                      <div>
                                        <h5 className="font-semibold text-slate-900 dark:text-white">{catalogSupplier.name}</h5>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">Codigo: {catalogSupplier.code}</p>
                                        {isAlreadyMapped && (
                                          <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                                            Vinculado a: {currentMapping}
                                          </p>
                                        )}
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                      {saveStatus === 'saving' && (
                                        <Loader2 className="w-4 h-4 text-primary-500 animate-spin" />
                                      )}
                                      {saveStatus === 'saved' && (
                                        <CheckCircle className="w-4 h-4 text-success-500" />
                                      )}
                                      <button
                                        onClick={() => {
                                          setCatalogMappings(prev => ({
                                            ...prev,
                                            [catalogSupplier.code]: selectedSupplier
                                          }))
                                          saveCatalogMappingMutation.mutate({
                                            catalogCode: catalogSupplier.code,
                                            factusolCode: selectedSupplier
                                          })
                                        }}
                                        disabled={saveCatalogMappingMutation.isPending}
                                        className="flex items-center gap-1 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                      >
                                        <Link2 className="w-4 h-4" />
                                        {isAlreadyMapped ? 'Reasignar aqui' : 'Vincular'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}

              {/* Tab Content: Pricing Margins */}
              {activeSupplierTab === 'margins' && (
                <div className="p-6 space-y-6">
                  {/* Info Banner */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-primary-600 dark:text-primary-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          <strong>Prioridad de margenes:</strong> Los margenes del proveedor tienen prioridad sobre los margenes de familia.
                          Si no configuras margenes para este proveedor, se usaran los de la familia del producto, o los globales.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Tariff Tabs */}
                  <div className="flex gap-2">
                    {[
                      { key: 'pvp' as TariffType, label: 'PVP', icon: Euro, color: 'green' },
                      { key: 'pvd' as TariffType, label: 'PVD', icon: Users, color: 'blue' },
                      { key: 'vip' as TariffType, label: 'VIP', icon: Crown, color: 'amber' },
                    ].map(({ key, label, icon: Icon, color }) => (
                      <button
                        key={key}
                        onClick={() => setActiveTariffTab(key)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                          activeTariffTab === key
                            ? `bg-${color}-100 dark:bg-${color}-900/30 text-${color}-700 dark:text-${color}-300 border border-${color}-300 dark:border-${color}-700`
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* Tiers Table */}
                  <div className="space-y-3">
                    <div className="grid grid-cols-4 gap-4 text-sm font-medium text-slate-500 dark:text-slate-400 px-2">
                      <div>Desde (€)</div>
                      <div>Hasta (€)</div>
                      <div>Margen (%)</div>
                      <div></div>
                    </div>

                    {(getCurrentPricingConfig()[`tiers_${activeTariffTab}` as keyof SupplierPricingConfig] as PricingTier[]).length === 0 ? (
                      <div className="text-center py-6 px-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-dashed border-slate-300 dark:border-slate-600">
                        <p className="text-slate-500 dark:text-slate-400 text-sm mb-2">
                          No hay márgenes configurados para este proveedor
                        </p>
                        <p className="text-slate-400 dark:text-slate-500 text-xs">
                          Se aplicarán los márgenes de familia correspondientes
                        </p>
                      </div>
                    ) : (
                      (getCurrentPricingConfig()[`tiers_${activeTariffTab}` as keyof SupplierPricingConfig] as PricingTier[]).map((tier, index) => (
                        <div key={index} className="grid grid-cols-4 gap-4 items-center">
                          <input
                            type="number"
                            value={tier.min}
                            onChange={(e) => handlePricingTierChange(index, 'min', e.target.value)}
                            className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                          <input
                            type="number"
                            value={tier.max}
                            onChange={(e) => handlePricingTierChange(index, 'max', e.target.value)}
                            className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                          <div className="relative">
                            <input
                              type="number"
                              value={tier.margin_percent}
                              onChange={(e) => handlePricingTierChange(index, 'margin_percent', e.target.value)}
                              className="px-3 py-2 pr-8 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 w-full"
                            />
                            <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          </div>
                          <button
                            onClick={() => handleRemovePricingTier(index)}
                            className="p-2 text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded-lg transition-colors"
                            title="Eliminar tramo"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    )}

                    <button
                      onClick={handleAddPricingTier}
                      className="flex items-center gap-2 px-4 py-2 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Añadir tramo
                    </button>
                  </div>

                  {/* Save Button */}
                  <div className="flex items-center justify-between pt-6 border-t border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-2">
                      {pricingSaveStatus === 'saving' && (
                        <span className="text-primary-500 text-sm flex items-center gap-1">
                          <Loader2 className="w-4 h-4 animate-spin" /> Guardando...
                        </span>
                      )}
                      {pricingSaveStatus === 'saved' && (
                        <span className="text-success-500 text-sm flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" /> Guardado
                        </span>
                      )}
                      {pricingSaveStatus === 'error' && (
                        <span className="text-danger-500 text-sm flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" /> Error
                        </span>
                      )}
                      {isPricingDirty && pricingSaveStatus === 'idle' && (
                        <span className="text-amber-500 text-sm">Cambios sin guardar</span>
                      )}
                    </div>
                    <button
                      onClick={handleSaveSupplierPricing}
                      disabled={saveSupplierPricingMutation.isPending || !isPricingDirty}
                      className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Save className="w-4 h-4" />
                      Guardar Margenes
                    </button>
                  </div>

                  {/* Status indicator */}
                  {hasSupplierPricingConfig(selectedSupplier) ? (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Este proveedor tiene margenes personalizados configurados</span>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <Info className="w-5 h-5" />
                        <span>Este proveedor usa los margenes de familia o globales. Modifica los valores y guarda para crear una configuracion personalizada.</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
